import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import type { CompanyVisit, VisitPhoto, VisitStatus } from '@/types';

const functions = getFunctions();
const storage = getStorage();

// ===========================================
// Types
// ===========================================

interface CreateVisitInput {
  companyId: string;
  visitDate: string; // ISO date string
  notes?: string;
  nextFollowUpDate?: string;
}

interface CreateVisitOutput {
  success: boolean;
  visitId?: string;
  error?: string;
}

interface GetVisitsOutput {
  success: boolean;
  visits?: CompanyVisit[];
  error?: string;
}

interface UpdateVisitInput {
  visitId: string;
  notes?: string;
  nextFollowUpDate?: string;
}

interface BasicOutput {
  success: boolean;
  error?: string;
}

interface AddPhotoInput {
  visitId: string;
  photo: {
    storagePath: string;
    downloadURL: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    description?: string;
  };
}

interface AddPhotoOutput {
  success: boolean;
  photoId?: string;
  error?: string;
}

export interface CompanyWithVisitStatus {
  id: string;
  name: string;
  type?: string;
  salesRepName?: string;
  lastVisitDate: string | null;
  lastVisitByName?: string;
  nextFollowUpDate: string | null;
  visitStatus: VisitStatus;
  daysSinceLastVisit?: number;
}

interface GetCompaniesWithStatusOutput {
  success: boolean;
  companies?: CompanyWithVisitStatus[];
  error?: string;
}

// Evidence Library Types
export interface VisitWithEvidence extends CompanyVisit {
  business: 'azdisaster' | 'blacksmith';
}

interface GetVisitsWithEvidenceInput {
  business?: 'azdisaster' | 'blacksmith' | 'both';
  limit?: number;
  searchQuery?: string;
}

interface GetVisitsWithEvidenceOutput {
  success: boolean;
  visits?: VisitWithEvidence[];
  total?: number;
  error?: string;
}

// ===========================================
// Visit CRUD Operations
// ===========================================

/**
 * Create a new visit record for a company
 */
export async function createVisit(input: CreateVisitInput): Promise<CreateVisitOutput> {
  const create = httpsCallable<CreateVisitInput, CreateVisitOutput>(
    functions,
    'crm_createCompanyVisit'
  );

  const result = await create(input);
  return result.data;
}

/**
 * Get all visits for a company
 */
export async function getCompanyVisits(
  companyId: string,
  limit: number = 20
): Promise<GetVisitsOutput> {
  const get = httpsCallable<{ companyId: string; limit: number }, GetVisitsOutput>(
    functions,
    'crm_getCompanyVisits'
  );

  const result = await get({ companyId, limit });
  return result.data;
}

/**
 * Update a visit record
 */
export async function updateVisit(input: UpdateVisitInput): Promise<BasicOutput> {
  const update = httpsCallable<UpdateVisitInput, BasicOutput>(
    functions,
    'crm_updateCompanyVisit'
  );

  const result = await update(input);
  return result.data;
}

/**
 * Delete a visit record
 */
export async function deleteVisit(visitId: string): Promise<BasicOutput> {
  const del = httpsCallable<{ visitId: string }, BasicOutput>(
    functions,
    'crm_deleteCompanyVisit'
  );

  const result = await del({ visitId });
  return result.data;
}

/**
 * Update follow-up date for a company
 */
export async function updateFollowUpDate(
  companyId: string,
  nextFollowUpDate: string | null
): Promise<BasicOutput> {
  const update = httpsCallable<{ companyId: string; nextFollowUpDate: string | null }, BasicOutput>(
    functions,
    'crm_updateFollowUpDate'
  );

  const result = await update({ companyId, nextFollowUpDate });
  return result.data;
}

// ===========================================
// Photo Upload
// ===========================================

/**
 * Upload a photo to Firebase Storage and add it to the visit
 */
export async function uploadVisitPhoto(
  companyId: string,
  visitId: string,
  file: File,
  description?: string
): Promise<AddPhotoOutput> {
  console.log('[uploadVisitPhoto] Starting upload:', {
    companyId,
    visitId,
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
  });

  try {
    // Generate unique filename
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${timestamp}_${safeName}`;
    const storagePath = `referral-crm/visit-photos/${companyId}/${visitId}/${fileName}`;

    console.log('[uploadVisitPhoto] Uploading to storage:', storagePath);

    // Upload to Firebase Storage
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);

    console.log('[uploadVisitPhoto] Storage upload complete, getting download URL');

    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);

    console.log('[uploadVisitPhoto] Download URL obtained, calling Cloud Function');

    // Add photo record to visit
    const addPhoto = httpsCallable<AddPhotoInput, AddPhotoOutput>(
      functions,
      'crm_addVisitPhoto'
    );

    const result = await addPhoto({
      visitId,
      photo: {
        storagePath,
        downloadURL,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        description,
      },
    });

    console.log('[uploadVisitPhoto] Cloud Function result:', result.data);

    return result.data;
  } catch (error: any) {
    console.error('[uploadVisitPhoto] Error:', {
      message: error.message,
      code: error.code,
      details: error.details,
      stack: error.stack,
    });
    return {
      success: false,
      error: error.message || 'Failed to upload photo',
    };
  }
}

// ===========================================
// Companies with Visit Status
// ===========================================

/**
 * Get all companies with their visit status (for visit tracking dashboard)
 */
export async function getCompaniesWithVisitStatus(
  greenMaxDays: number = 30,
  yellowMaxDays: number = 60
): Promise<GetCompaniesWithStatusOutput> {
  const get = httpsCallable<
    { greenMaxDays: number; yellowMaxDays: number },
    GetCompaniesWithStatusOutput
  >(functions, 'crm_getCompaniesWithVisitStatus');

  const result = await get({ greenMaxDays, yellowMaxDays });
  return result.data;
}

/**
 * Get all visits that have evidence photos (for Evidence Library)
 */
export async function getAllVisitsWithEvidence(
  input: GetVisitsWithEvidenceInput = {}
): Promise<GetVisitsWithEvidenceOutput> {
  const get = httpsCallable<GetVisitsWithEvidenceInput, GetVisitsWithEvidenceOutput>(
    functions,
    'crm_getAllVisitsWithEvidence'
  );

  const result = await get(input);
  return result.data;
}

// ===========================================
// Utility Functions
// ===========================================

/**
 * Calculate visit status based on days since last visit
 */
export function calculateVisitStatus(
  lastVisitDate: Date | string | null,
  greenMaxDays: number = 30,
  yellowMaxDays: number = 60
): { status: VisitStatus; daysSince: number | null } {
  if (!lastVisitDate) {
    return { status: 'red', daysSince: null };
  }

  const lastVisit = typeof lastVisitDate === 'string' ? new Date(lastVisitDate) : lastVisitDate;
  const now = new Date();
  const daysSince = Math.floor((now.getTime() - lastVisit.getTime()) / (1000 * 60 * 60 * 24));

  let status: VisitStatus;
  if (daysSince <= greenMaxDays) {
    status = 'green';
  } else if (daysSince <= yellowMaxDays) {
    status = 'yellow';
  } else {
    status = 'red';
  }

  return { status, daysSince };
}

/**
 * Get status color classes for display
 */
export function getVisitStatusColor(status: VisitStatus): {
  bg: string;
  text: string;
  dot: string;
} {
  switch (status) {
    case 'green':
      return {
        bg: 'bg-green-100',
        text: 'text-green-700',
        dot: 'bg-green-500',
      };
    case 'yellow':
      return {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        dot: 'bg-yellow-500',
      };
    case 'red':
      return {
        bg: 'bg-red-100',
        text: 'text-red-700',
        dot: 'bg-red-500',
      };
  }
}

/**
 * Format days since last visit for display
 */
export function formatDaysSinceVisit(days: number | null | undefined): string {
  if (days === null || days === undefined) {
    return 'Never visited';
  }
  if (days === 0) {
    return 'Today';
  }
  if (days === 1) {
    return '1 day ago';
  }
  return `${days} days ago`;
}
